import PageLayout from "@/react/features/styled/PageLayout";
import { CustomerCard } from "@/react/features/customer";
import FullWidthDiv from "@/react/features/styled/FullWidthDiv";
import styled from "styled-components";
import {
  formatGatewayFields,
  formatSubscriberDetails,
} from "../formatGatewayDetails";
import { TrunkGatewayField } from "../components/TrunkGatewayField";
import { IpBox } from "../components/IpBox";
import React, { useMemo } from "react";
import {
  FieldGroupContainer,
  IpBoxHeader,
  Label,
  StyledButton,
  StyledButtonGroup,
  StyledPlusIcon,
} from "../styled";
import { withTrunkGateway } from "@/react/features/support-tools/siptrunk/withTrunkGateway";
import { useTrunkGatewayContext } from "@/react/features/support-tools/siptrunk/TrunkGatewayContext";
import { IP_BOXES_KEY } from "@/react/features/support-tools/siptrunk/constants";
import BreadcrumbsHeader from "@/react/features/support-tools/siptrunk/components/BreadcrumbsHeader";
import { GatewayDidsTable } from "@/react/features/support-tools/siptrunk/dids/GatewayDidsTable";
import { COLORS } from "@/react/theme/constants";
import { Button } from "@/react/styled";
import { useUpdateSipTrunkMutation } from "@/react/features/support-tools/siptrunk/api";
import { OverlaySpinner } from "@/react/components/OverlaySpinner";
import { DURATION, notify, TYPE } from "@8x8/oxygen-toaster";
import { errorNotification } from "@/react/notifications";
import lodash from "lodash";

const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(520px, 600px) 1fr;
  gap: 40px;
  margin-bottom: 40px;
`;

const LeftSection = styled.div`
  max-width: 600px;
  padding-right: 40px;
  border-right: 1px solid ${COLORS.tertiary};
`;

const RightSection = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TrunkGatewayEditPage = () => {
  const {
    state: trunkGateway,
    fetchedTrunkGateway,
    addIpBox,
    resetTrunkGateway,
    isValid,
  } = useTrunkGatewayContext();

  const [updateSipTrunk, { isLoading: isUpdating }] =
    useUpdateSipTrunkMutation();

  const isChanged = useMemo(
    () => !lodash.isEqual(trunkGateway, fetchedTrunkGateway),
    [trunkGateway, fetchedTrunkGateway]
  );

  const updateTrunkGateway = () => {
    updateSipTrunk({ trunkId: trunkGateway.trunkId, trunkGateway })
      .then((res) => {
        if (res.error) {
          notify({
            content: `Gateway could not be updated: ${res.error?.data?.message}`,
            type: TYPE.error,
            duration: DURATION.INFINITE,
          });
        } else {
          notify({
            content: `Gateway ${trunkGateway.name} updated successfully.`,
            type: TYPE.success,
            duration: 5000,
          });
        }
      })
      .catch((e) => errorNotification(e));
  };

  return (
    <PageLayout>
      <CustomerCard />
      <FullWidthDiv>
        <BreadcrumbsHeader trunkGatewayName={trunkGateway?.name} />
        <ContentContainer>
          <LeftSection>
            <FieldGroupContainer>
              {trunkGateway &&
                formatGatewayFields(trunkGateway).map((field) => (
                  <TrunkGatewayField key={field.label} {...field} />
                ))}
              <IpBoxHeader>
                <Label>IP Boxes</Label>
                <StyledButton
                  variant={"text"}
                  onClick={() => addIpBox({ boxType: IP_BOXES_KEY.BOX })}
                >
                  <StyledPlusIcon /> Add IP Box
                </StyledButton>
              </IpBoxHeader>
              {trunkGateway &&
                (trunkGateway.boxes || []).map((box, index) => (
                  <IpBox boxType={IP_BOXES_KEY.BOX} index={index} {...box} />
                ))}

              <IpBoxHeader>
                <Label>Additional Outbound IP Boxes</Label>
                <StyledButton
                  variant={"text"}
                  onClick={() =>
                    addIpBox({ boxType: IP_BOXES_KEY.ADDITIONAL_BOX })
                  }
                >
                  <StyledPlusIcon /> Add IP Box
                </StyledButton>
              </IpBoxHeader>
              {trunkGateway &&
                (trunkGateway.additionalBoxes || []).map((box, index) => (
                  <IpBox
                    boxType={IP_BOXES_KEY.ADDITIONAL_BOX}
                    index={index}
                    {...box}
                  />
                ))}
            </FieldGroupContainer>

            <hr />
            <FieldGroupContainer>
              {trunkGateway?.subscriberDetails &&
                formatSubscriberDetails(trunkGateway.subscriberDetails).map(
                  (field) => <TrunkGatewayField key={field.label} {...field} />
                )}
            </FieldGroupContainer>
            <StyledButtonGroup align="left">
              <Button
                variant={"primary"}
                onClick={updateTrunkGateway}
                isDisabled={!isValid || !isChanged}
              >
                Save
              </Button>
              <Button
                variant={"secondary"}
                onClick={() => resetTrunkGateway(fetchedTrunkGateway)}
                isDisabled={!isChanged}
              >
                Reset
              </Button>
            </StyledButtonGroup>
          </LeftSection>
          <RightSection>
            <Label>Gateway DIDs</Label>
            <GatewayDidsTable />
          </RightSection>
        </ContentContainer>
        {isUpdating && <OverlaySpinner />}
      </FullWidthDiv>
    </PageLayout>
  );
};

export default withTrunkGateway(TrunkGatewayEditPage);
